import * as React from 'react';
import cx from 'classnames';
import numeral from 'numeral';

import { ArrowDownFilledIcon } from 'src/icons';

import styles from './Video.scss';

export interface IVideoProps {
  src: string;

  showDuration?: boolean;
  onMouseDown?();
  onDataLoaded?();
  onDimensionDetected?(videoWidth: number, videoHeight: number);
  onError?();

  // <video /> related config
  autoPlay?: boolean;
  loop?: boolean;
  poster?: string;
  controls?: boolean;

  classNames?: string[];
}

type TDefaultProp =
  | 'showDuration'
  | 'onMouseDown'
  | 'onDataLoaded'
  | 'onDimensionDetected'
  | 'onError'
  | 'autoPlay'
  | 'loop'
  | 'classNames'
  | 'controls';

interface IState {
  duration: number;
}

/**
 * @class
 * @extends {React.Component}
 */
export class Video extends React.PureComponent<IProps, IState> {
  public static defaultProps: Pick<IProps, TDefaultProp> = {
    showDuration: false,
    onMouseDown: () => undefined,
    onDataLoaded: () => undefined,
    onDimensionDetected: () => undefined,
    onError: () => undefined,

    autoPlay: false,
    loop: false,
    controls: false,

    classNames: [],
  };

  private videoRef: React.RefObject<HTMLVideoElement>;

  /**
   * @inheritDoc
   */
  constructor(props: IProps) {
    super(props);

    this.videoRef = React.createRef();

    this.state = {
      duration: null,
    };
  }

  /**
   * @inheritdoc
   */
  public UNSAFE_componentWillUnmount() {
    const video = this.videoRef.current;

    if (video && !video.paused) {
      video.pause();
    }
  }

  /**
   * @inheritdoc
   */
  public render() {
    const {
      src,
      showDuration,
      onMouseDown,
      autoPlay,
      loop,
      classNames,
      poster,
      controls,
    } = this.props;
    const { duration } = this.state;

    return (
      <div className={cx(classNames.concat(styles.Video))} onMouseDown={onMouseDown}>
        {showDuration && duration && (
          <div className={styles.duration}>
            <ArrowDownFilledIcon className={styles.playIcon} size={10} />
            {numeral(duration).format('00:00:00')}
          </div>
        )}
        <video
          ref={this.videoRef}
          className={cx(styles.video)}
          onLoadedData={this.onVideoDataLoaded}
          onError={this.onError}
          autoPlay={autoPlay}
          loop={loop}
          poster={poster}
          controls={controls}
        >
          {['webm', 'mp4', 'ogg'].map((type) => (
            <source key={type} src={src} type={`video/${type}`} />
          ))}
        </video>
      </div>
    );
  }

  /**
   * @private
   * Called when Video's dimension is fixed, either after loaded or failed.
   */
  private onDimensionDetected = () => {
    const { onDimensionDetected } = this.props;
    const video = this.videoRef.current;

    // TODO: figure out under what circumstances video is not mounted
    if (!video) {
      return;
    }

    const { videoWidth: width, videoHeight: height } = this.videoRef.current;

    onDimensionDetected(width, height);
  };

  /**
   * @private
   * Handler for when the video data is loaded.
   */
  private onVideoDataLoaded = () => {
    const { onDataLoaded } = this.props;
    const video = this.videoRef.current;

    if (!video) {
      return;
    }

    this.onDimensionDetected();
    onDataLoaded();

    this.setState({
      duration: video.duration,
    });
  };

  /**
   * @private
   * Video load error due to invalid src or timeout.
   * Also call onDataLoaded.
   */
  private onError = () => {
    const { onDataLoaded, onError } = this.props;

    this.onDimensionDetected();
    onDataLoaded();
    onError();
  };
}
